// First-run onboarding tour — a lightweight, dependency-free coachmark walk
// through the main navigation. Shows once per browser (remembered in
// localStorage), is fully skippable, and degrades gracefully: any step whose
// target is missing falls back to a centred card. No nags — if the user has
// seen it, it never appears again.

const SEEN_KEY = "verde_onboarded";

const STEPS = [
  {
    target: null,
    title: "Welcome to Verde 🌿",
    body: "Your clean, offline-ready Spanish reference. Here's a 20-second tour — or skip it anytime.",
  },
  {
    target: '[data-tour="map"]',
    title: "Start with Explore",
    body: "The beginner building blocks — the essentials, word building, sentence making, and the Big 4 verbs.",
  },
  {
    target: '[data-tour="search"]',
    title: "Jump anywhere instantly",
    body: "Press ⌘K (or Ctrl K) to search every verb, word, and article from any page — no need to navigate.",
  },
  {
    target: '[data-tour="verbs"]',
    title: "Dig into The Vault",
    body: "The full library when you want it — the complete verb index, the grammar map, and the dictionary.",
  },
  {
    target: '[data-tour="practice"]',
    title: "Practise when ready",
    body: "Flip flashcards, fill the gaps, and review what's due — no scores, no pressure.",
  },
  {
    target: '[data-tour="auth"]',
    title: "Make it yours (optional)",
    body: "Sign in to pin items and keep personal notes. Everything's readable as a guest, too.",
  },
];

export function hasSeenOnboarding() {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* private mode / storage disabled — tour just shows again next visit */
  }
}

// Show the tour only on a first visit. Call after the shell is mounted.
export function maybeStartOnboarding() {
  if (hasSeenOnboarding()) return;
  // Defer one frame so the header has laid out and targets have real rects.
  requestAnimationFrame(() => startTour());
}

export function startTour() {
  let idx = 0;

  const root = document.createElement("div");
  root.className = "fixed inset-0 z-[60]";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.innerHTML = `
    <div class="tour-ring pointer-events-none absolute rounded-xl transition-all duration-200"
      style="box-shadow: 0 0 0 9999px rgba(42,63,42,0.55); top:0;left:0;width:0;height:0;"></div>
    <div class="tour-pop absolute max-w-xs w-[20rem] bg-white rounded-2xl border border-sage-100 shadow-xl p-4 transition-all duration-200">
      <p class="tour-title font-bold text-sage-800 mb-1"></p>
      <p class="tour-body text-sm text-sage-600 mb-4 leading-relaxed"></p>
      <div class="flex items-center justify-between">
        <button class="tour-skip text-xs text-sage-400 hover:text-sage-600 underline">Skip tour</button>
        <div class="flex items-center gap-2">
          <span class="tour-count text-xs text-sage-400 mr-1"></span>
          <button class="tour-next bg-sage-500 hover:bg-sage-600 text-white text-sm px-4 py-1.5 rounded-lg font-medium"></button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(root);

  const ring = root.querySelector(".tour-ring");
  const pop = root.querySelector(".tour-pop");
  const titleEl = root.querySelector(".tour-title");
  const bodyEl = root.querySelector(".tour-body");
  const countEl = root.querySelector(".tour-count");
  const nextBtn = root.querySelector(".tour-next");
  const skipBtn = root.querySelector(".tour-skip");

  function finish() {
    markSeen();
    window.removeEventListener("keydown", onKey);
    window.removeEventListener("resize", render);
    root.remove();
  }

  function onKey(e) {
    if (e.key === "Escape") finish();
    else if (e.key === "Enter") next();
  }

  function next() {
    if (idx >= STEPS.length - 1) finish();
    else {
      idx++;
      render();
    }
  }

  function placePopup(rect) {
    const margin = 12;
    const pw = pop.offsetWidth || 320;
    const ph = pop.offsetHeight || 160;
    let top, left;
    if (rect) {
      // Prefer below the target; flip above if it would overflow.
      top = rect.bottom + margin;
      if (top + ph > window.innerHeight - margin) top = rect.top - ph - margin;
      if (top < margin) top = margin;
      left = rect.left + rect.width / 2 - pw / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - pw - margin));
    } else {
      // Centred fallback for targetless steps.
      top = window.innerHeight / 2 - ph / 2;
      left = window.innerWidth / 2 - pw / 2;
    }
    pop.style.top = `${top}px`;
    pop.style.left = `${left}px`;
  }

  function render() {
    const step = STEPS[idx];
    titleEl.textContent = step.title;
    bodyEl.textContent = step.body;
    countEl.textContent = `${idx + 1} / ${STEPS.length}`;
    nextBtn.textContent = idx === STEPS.length - 1 ? "Done" : "Next";

    const el = step.target ? document.querySelector(step.target) : null;
    if (el) {
      const r = el.getBoundingClientRect();
      const pad = 6;
      ring.style.top = `${r.top - pad}px`;
      ring.style.left = `${r.left - pad}px`;
      ring.style.width = `${r.width + pad * 2}px`;
      ring.style.height = `${r.height + pad * 2}px`;
      ring.style.opacity = "1";
      placePopup(r);
    } else {
      // No target: collapse the spotlight to nothing, dim the whole screen.
      ring.style.width = "0px";
      ring.style.height = "0px";
      ring.style.top = "50%";
      ring.style.left = "50%";
      placePopup(null);
    }
  }

  nextBtn.addEventListener("click", next);
  skipBtn.addEventListener("click", finish);
  window.addEventListener("keydown", onKey);
  window.addEventListener("resize", render);
  render();
}
